/**
 * Скачивание PDF рецепта интерактива (рис / плов): fetch → blob → программный клик по <a download>.
 * В разметке используется настоящий `<a href :download>` (удобно для «Открыть в новой вкладке» и SEO),
 * но у **чужого origin** атрибут `download` браузером часто игнорируется — тогда без fetch+Blob PDF откроется во вкладке.
 */

export function riceReceiptAbsoluteUrl(path) {
  if (!path || typeof path !== 'string') return '';
  const p = path.trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
  return `${base}${p.startsWith('/') ? '' : '/'}${p}`;
}

/**
 * Относительный или абсолютный путь к PDF рецепта из строки won-rice / final_round / нормализованной карточки.
 */
export function resolveRiceRecipePdfPath(row) {
  if (!row || typeof row !== 'object') return '';
  const raw = row.__raw && typeof row.__raw === 'object' ? row.__raw : {};
  const candidates = [
    row.receipt,
    row.recipe_pdf,
    row.recipe_url,
    raw.receipt,
    raw.recipe_pdf,
    raw.recipe_url,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
}

export function riceRecipeFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/"/g, '').trim());
    } catch {
      return null;
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1].trim();
  const plain = /filename\s*=\s*([^;\s]+)/i.exec(header);
  return plain?.[1]?.replace(/"/g, '').trim() || null;
}

export function riceRecipeSafeDownloadName(title, fallbackBase) {
  const raw = String(title || '').replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, ' ').trim();
  const base = (raw || fallbackBase).slice(0, 120);
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
}

/**
 * @param {string} url — абсолютный URL PDF
 * @param {string} [titleForFilename] — имя файла без расширения (подставляется если нет Content-Disposition)
 */
export async function downloadRiceRecipePdf(url, titleForFilename = '') {
  if (!url || typeof url !== 'string') return;
  const fallbackName = riceRecipeSafeDownloadName(titleForFilename, 'retsept');
  let res;
  try {
    res = await fetch(url, {
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
      headers: { Accept: 'application/pdf,*/*' },
    });
    if (!res.ok) {
      res = await fetch(url, {
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
        headers: { Accept: 'application/pdf,*/*' },
      });
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    console.warn('Rice recipe: fetch failed, opening URL in a new tab', e);
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  let objectUrl;
  try {
    const buf = await res.arrayBuffer();
    const blob = new Blob([buf], { type: 'application/pdf' });
    objectUrl = URL.createObjectURL(blob);
    const fromCd = riceRecipeFilenameFromContentDisposition(res.headers.get('content-disposition'));
    let filename = fromCd || fallbackName;
    if (fromCd && !/\.pdf$/i.test(filename)) filename = `${filename}.pdf`;
    filename = filename.replace(/[/\\?%*:|"<>]/g, '-');

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, 60_000);
  } catch (e) {
    console.warn('Rice recipe: blob download failed, opening URL in a new tab', e);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
